import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import Header from "@/components/Header";
import { FeaturedProductCard } from "@/components/FeaturedProductCard";
import { ShoppingCart } from "@/components/ShoppingCart";
import { useCart } from "@/hooks/useCart";
import { useAuth } from "@/hooks/useAuth";
import { useSavedItems } from "@/hooks/useSavedItems";
import { useBuyerLocation } from "@/hooks/useBuyerLocation";
import { CategoryChips, BROWSE_CATEGORIES } from "@/components/CategoryChips";
import { HomeHeroCard } from "@/components/HomeHeroCard";
import { CountdownBanner } from "@/components/CountdownBanner";
import { ListFirstProductBanner } from "@/components/ListFirstProductBanner";
import { haversineMiles, type ResolvedLocation } from "@/lib/geo";
import { ChevronRight, Sprout, Users, Sparkles } from "lucide-react";
import type { Item, User, CartItemWithDetails } from "@shared/schema";
import { formatWeight } from "@shared/weight";

interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  onSeeAll?: () => void;
  accent?: "primary" | "secondary";
}

function SectionHeader({ title, subtitle, onSeeAll, accent = "primary" }: SectionHeaderProps) {
  const accentClass = accent === "secondary" ? "text-brand-secondary" : "text-primary";
  return (
    <div className="mb-3 flex items-end justify-between gap-3">
      <div className="min-w-0">
        <h2 className="font-display text-lg font-bold leading-tight tracking-tight text-foreground sm:text-xl">
          {title}
        </h2>
        {subtitle && (
          <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">{subtitle}</p>
        )}
      </div>
      {onSeeAll && (
        <button
          type="button"
          onClick={onSeeAll}
          className={`flex shrink-0 items-center gap-0.5 text-sm font-semibold ${accentClass} hover:underline underline-offset-2`}
          data-testid={`link-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
        >
          See all
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

interface SellerPreviewCardProps {
  seller: User;
}

function SellerPreviewCard({ seller }: SellerPreviewCardProps) {
  const [, setLocation] = useLocation();
  // Show the maker's display (business) name everywhere; the real name only
  // appears on their own profile.
  const name =
    seller.displayName?.trim() ||
    `${seller.firstName ?? ""} ${seller.lastName ?? ""}`.trim() ||
    "Local Producer";
  const initials =
    name
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "LP";
  return (
    <button
      type="button"
      onClick={() => setLocation(`/u/${seller.id}`)}
      className="flex w-32 shrink-0 flex-col items-center gap-2 rounded-2xl bg-card p-3 text-center shadow-sm transition hover:shadow-md sm:w-36"
      data-testid={`card-grower-${seller.id}`}
    >
      <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-brand-secondary/15 text-brand-secondary">
        {seller.profileImageUrl ? (
          <img src={seller.profileImageUrl} alt={name} loading="lazy" decoding="async" className="h-full w-full object-cover" />
        ) : (
          <span className="text-lg font-bold">{initials}</span>
        )}
      </div>
      <div className="min-w-0 w-full">
        <p className="truncate text-sm font-semibold text-foreground">{name}</p>
        {seller.zipCode && (
          <p className="truncate text-xs text-muted-foreground">ZIP {seller.zipCode}</p>
        )}
      </div>
    </button>
  );
}

interface ModeTileProps {
  label: string;
  sublabel: string;
  icon: typeof Sprout;
  bgClass: string;
  iconColorClass: string;
  onClick: () => void;
  testId: string;
}

function ModeTile({ label, sublabel, icon: Icon, bgClass, iconColorClass, onClick, testId }: ModeTileProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col gap-2 rounded-2xl p-4 text-left transition ${bgClass}`}
      data-testid={testId}
    >
      <Icon className={`h-7 w-7 ${iconColorClass}`} strokeWidth={1.6} />
      <div>
        <p className="font-semibold text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground">{sublabel}</p>
      </div>
    </button>
  );
}

interface HomeFeedProps {
  /** Rendered inside a SwipeTabs panel: skip own Header/nav/cart chrome, take
   *  buyerLocation from the shared wrapper, and switch tabs via onNavigateMode
   *  instead of a full-page navigation. */
  embedded?: boolean;
  buyerLocation?: ResolvedLocation | null;
  zipCode?: string;
  onNavigateMode?: (mode: string, extra?: Record<string, string>) => void;
}

export default function HomeFeed({
  embedded = false,
  buyerLocation: sharedBuyerLocation = null,
  zipCode: sharedZip,
  onNavigateMode,
}: HomeFeedProps = {}) {
  const { user } = useAuth();
  const { cart, updateQuantity, removeFromCart, clearCart, cartCount } = useCart();
  const { savedSet, toggleSave } = useSavedItems();
  const [, setLocation] = useLocation();
  const [isCartOpen, setIsCartOpen] = useState(false);

  const zipCode = embedded ? (sharedZip ?? "").trim() : (user?.zipCode ?? "").trim();
  const ownLocation = useBuyerLocation(zipCode);
  const buyerLocation = embedded ? sharedBuyerLocation : ownLocation.buyerLocation;
  const { isLocating, locationError, handleUseCurrentLocation } = ownLocation;

  const hasAutoLocatedRef = useRef(false);
  useEffect(() => {
    if (embedded) return;
    if (hasAutoLocatedRef.current) return;
    if (buyerLocation?.point) return;
    hasAutoLocatedRef.current = true;
    void handleUseCurrentLocation();
  }, [embedded, buyerLocation, handleUseCurrentLocation]);

  // "Near you" is a true closest-first sort, so fetch all published items and
  // rank them by distance client-side (below). We deliberately do NOT filter to
  // the buyer's exact ZIP here — that hid nearby makers in an adjacent ZIP (e.g.
  // an Albany buyer never saw an Albany maker because their account ZIP differed).
  const { data: items = [], isLoading: isLoadingItems, isError: itemsError, refetch: refetchItems } = useQuery<(Item & { seller: User })[]>({
    queryKey: ["/api/items", "all"],
    queryFn: async () => {
      const res = await fetch("/api/items", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const { data: producers = [] } = useQuery<User[]>({
    queryKey: ["/api/sellers"],
    queryFn: async () => {
      const res = await fetch("/api/sellers", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const { data: cartItemsDetails = [] } = useQuery<CartItemWithDetails[]>({
    queryKey: ["/api/cart/details", JSON.stringify(cart)],
    enabled: cart.length > 0,
    queryFn: async () => {
      const res = await fetch(`/api/cart/details?cart=${encodeURIComponent(JSON.stringify(cart))}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  // Fall back to the signed-in user's geocoded home coordinates when there's no
  // live GPS fix, so proximity sorting still works when location is denied/off.
  const storedUserPoint = (() => {
    const lat = (user as any)?.latitude;
    const lng = (user as any)?.longitude;
    if (lat == null || lng == null || lat === "" || lng === "") return null;
    return { latitude: Number(lat), longitude: Number(lng) };
  })();
  const buyerPoint = buyerLocation?.point ?? storedUserPoint;
  const distanceToSeller = (seller: User | null | undefined) => {
    if (!buyerPoint || !seller) return null;
    const lat = (seller as any)?.latitude;
    const lng = (seller as any)?.longitude;
    if (lat == null || lng == null || lat === "" || lng === "") return null;
    return haversineMiles(buyerPoint, { latitude: Number(lat), longitude: Number(lng) });
  };
  const sellerDistance = (item: Item & { seller: User }) => distanceToSeller(item.seller);
  const byDistance = (a: number | null, b: number | null) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return a - b;
  };
  const isFeaturedSeller = (seller: User | null | undefined) => {
    const until = (seller as any)?.featuredUntil;
    return until ? new Date(until).getTime() > Date.now() : false;
  };
  // Curated launch cohort, shown as a location-agnostic spotlight shelf with
  // featured sellers first. Kept separate from the proximity list below so
  // "Near you" stays an honest closest-first sort.
  // Order the shelf by featuredUntil (later date = higher priority), so the
  // sellers we want up front can be pinned by setting a further-out
  // featuredUntil. Non-featured founding makers fall to the end.
  const featuredRank = (seller: User | null | undefined) =>
    isFeaturedSeller(seller) ? new Date((seller as any).featuredUntil).getTime() : 0;
  const foundingMakers = producers
    .filter((s) => Number((s as any).isFoundingMaker ?? 0) === 1)
    .slice()
    .sort((a, b) => featuredRank(b) - featuredRank(a));
  const nearbyItems = items
    .filter((item) => item.qtyAvailable > 0)
    .slice()
    .sort((a, b) => (buyerPoint ? byDistance(sellerDistance(a), sellerDistance(b)) : 0))
    .slice(0, 8);

  // "Growers & makers near you" is a true closest-first sort, not the raw
  // /api/sellers order (which is location-agnostic, so far-away makers surfaced
  // first regardless of where the buyer is).
  const featuredProducers = producers
    .slice()
    .sort((a, b) => (buyerPoint ? byDistance(distanceToSeller(a), distanceToSeller(b)) : 0))
    .slice(0, 5);
  const { data: presentCategories = [] } = useQuery<string[]>({
    queryKey: ["/api/item-categories"],
    queryFn: async () => {
      const res = await fetch("/api/item-categories", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });
  const categories = BROWSE_CATEGORIES.filter((c) => presentCategories.includes(c));

  const goToMode = (mode: string, extra?: Record<string, string>) => {
    if (embedded) {
      onNavigateMode?.(mode, extra);
      return;
    }
    const params = new URLSearchParams({ mode, ...(extra ?? {}) });
    if (typeof window !== "undefined") {
      window.location.assign(`/?${params.toString()}`);
    }
  };

  return (
    <div className={embedded ? "" : "min-h-screen flex flex-col bg-background"}>
      {!embedded && (
        <Header
          cartCount={cartCount}
          onCartClick={() => setIsCartOpen(true)}
          collapsed={false}
          locationLabel={
            buyerLocation?.source === "browser_gps"
              ? buyerLocation.label || "Near you"
              : zipCode
                ? `ZIP ${zipCode}`
                : "Set location"
          }
          onUseCurrentLocation={handleUseCurrentLocation}
          isLocating={isLocating}
          locationError={locationError}
        />
      )}

      <main className={embedded ? "pb-8" : "flex-1 pb-8"}>
        <CountdownBanner />
        <ListFirstProductBanner />

        <HomeHeroCard />

        {/* Categories */}
        <section className="container mx-auto px-4 pt-5">
          <SectionHeader
            title="Shop by category"
            subtitle="Find what's in season near you"
            accent="secondary"
          />
        </section>
        {categories.length > 0 && (
          <CategoryChips
            categories={categories}
            selectedCategory={null}
            onSelectCategory={(cat) => {
              if (cat) goToMode("products", { category: cat });
              else goToMode("products");
            }}
          />
        )}

        {/* Founding Makers — curated launch spotlight, shown to everyone */}
        {foundingMakers.length > 0 && (
          <section className="container mx-auto px-4 pt-7">
            <SectionHeader
              title="Founding Makers"
              subtitle="The first neighbors to open up shop on Stoop"
              onSeeAll={() => goToMode("producers")}
              accent="primary"
            />
            <div className="flex gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              {foundingMakers.map((seller) => (
                <SellerPreviewCard key={seller.id} seller={seller} />
              ))}
            </div>
          </section>
        )}

        {/* Near you — honest closest-first */}
        <section className="container mx-auto px-4 pt-7">
          <SectionHeader
            title="Near you"
            subtitle={
              buyerPoint && buyerLocation?.label
                ? `Closest to ${buyerLocation.label}`
                : buyerPoint
                  ? "Closest to you"
                  : zipCode
                    ? `Around ${zipCode}`
                    : "Fresh picks from your area"
            }
            onSeeAll={() => goToMode("products")}
            accent="primary"
          />
          {isLoadingItems ? (
            <div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="aspect-[3/4] w-[60%] shrink-0 animate-pulse rounded-2xl bg-muted sm:w-[40%]"
                />
              ))}
            </div>
          ) : itemsError && nearbyItems.length === 0 ? (
            // Distinct from the empty state below: a dropped connection must not
            // read as an empty neighborhood.
            <div className="rounded-2xl border border-dashed border-border p-8 text-center">
              <p className="text-sm text-muted-foreground">
                {typeof navigator !== "undefined" && navigator.onLine === false
                  ? "You're offline. Listings will load once you have a connection."
                  : "Couldn't load listings just now."}
              </p>
              <button
                type="button"
                onClick={() => void refetchItems()}
                className="mt-3 text-sm font-medium text-primary underline"
                data-testid="button-retry-feed"
              >
                Try again
              </button>
            </div>
          ) : nearbyItems.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-8 text-center">
              <p className="text-sm text-muted-foreground">
                No listings near you yet. Check back soon — your neighborhood is just getting started.
              </p>
            </div>
          ) : (
            <div className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              {nearbyItems.map((item) => {
                const soldByWeight = Boolean((item as any).soldByWeight);
                const wMin = (item as any).weightMin as number | null | undefined;
                const wMax = (item as any).weightMax as number | null | undefined;
                const tier = (item as any).sizeTier as string | null | undefined;
                const rangeLabel =
                  soldByWeight && wMin != null && wMax != null
                    ? wMin === wMax
                      ? formatWeight(wMin)
                      : `${formatWeight(wMin)}–${formatWeight(wMax)}`
                    : null;
                return (
                  <div key={item.id} className="relative w-[60%] shrink-0 snap-start sm:w-[40%] md:w-[28%]">
                    {soldByWeight && (tier || rangeLabel) && (
                      <div className="pointer-events-none absolute left-2 top-2 z-10 rounded-full bg-primary/90 px-2 py-0.5 text-[10px] font-semibold text-primary-foreground shadow-sm">
                        {[tier, rangeLabel].filter(Boolean).join(" · ")}
                      </div>
                    )}
                    <FeaturedProductCard
                      item={item}
                      buyerLocation={buyerLocation}
                      fallbackBuyerZip={zipCode || null}
                      isSaved={savedSet.has(item.id)}
                      onToggleSave={() => toggleSave(item.id)}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Growers near you */}
        {featuredProducers.length > 0 && (
          <section className="container mx-auto px-4 pt-7">
            <SectionHeader
              title="Growers & makers near you"
              subtitle="The neighbors who grow and craft what you'll eat"
              onSeeAll={() => goToMode("producers")}
              accent="secondary"
            />
            <div className="flex gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              {featuredProducers.map((seller) => (
                <SellerPreviewCard key={seller.id} seller={seller} />
              ))}
            </div>
          </section>
        )}

        {/* Browse modes link card */}
        <section className="container mx-auto px-4 pt-7">
          <div className="grid grid-cols-2 gap-3">
            <ModeTile
              label="Weekly boxes"
              sublabel="Curated bundles"
              icon={Sprout}
              bgClass="bg-brand-secondary/15 hover:bg-brand-secondary/20"
              iconColorClass="text-brand-secondary"
              onClick={() => goToMode("boxes")}
              testId="card-mode-boxes"
            />
            <ModeTile
              label="Growers & makers"
              sublabel="Browse by seller"
              icon={Users}
              bgClass="bg-primary/10 hover:bg-primary/15"
              iconColorClass="text-primary"
              onClick={() => goToMode("producers")}
              testId="card-mode-producers"
            />
            <ModeTile
              label="Saved"
              sublabel="Items you liked"
              icon={Sparkles}
              bgClass="bg-muted hover:bg-muted/80"
              iconColorClass="text-foreground/70"
              onClick={() => goToMode("saved")}
              testId="card-mode-saved"
            />
            <ModeTile
              label="Following"
              sublabel="Sellers you follow"
              icon={Users}
              bgClass="bg-card hover:bg-muted border border-border"
              iconColorClass="text-foreground/70"
              onClick={() => goToMode("following")}
              testId="card-mode-following"
            />
          </div>
        </section>
      </main>

      {!embedded && (
        <ShoppingCart
          isOpen={isCartOpen}
          onClose={() => setIsCartOpen(false)}
          items={cartItemsDetails}
          onUpdateQuantity={updateQuantity}
          onRemove={removeFromCart}
          onClearCart={clearCart}
          onCheckout={() => {
            setIsCartOpen(false);
            setLocation("/checkout");
          }}
        />
      )}
    </div>
  );
}
